import React from "react"
import styled from "styled-components"
import { Field, FieldProps } from "react-final-form"

import { Paragraph } from "components/text"
import Icon from "components/icon"
import { getValidator } from "components/form"
import { DefaultFieldProps } from "components/form/shared/default-field"
import { Error } from "components/form/shared/default-field/styles"

import * as Default from "./styles/default"
import * as WithImage from "./styles/with-image"

const Input = styled.input`
  /*
    This positions the input right behind the square that mimicks it
  */
  position: absolute;
  opacity: 0;
`

function CheckboxInput({
  view = "default",
  label,
  input,
  meta,
  imageChecked,
  imageUnchecked,
  dataTest,
  onChange
}: DefaultFieldProps<string> & CheckboxViewProps) {
  const { error, submitFailed } = meta
  const displayError = error && submitFailed
  const { checked } = input
  const id = `${input.name}--${input.value}`

  const inputProps = {
    id,
    ...input,
    /* If custom onChange handler is passed we chain it to the default onChange handler */
    onChange: (e: React.ChangeEvent) => {
      input.onChange(e)
      onChange && onChange(e)
    }
  }

  const labelProps = {
    htmlFor: id,
    checked,
    error: displayError
  }

  switch (view) {
    case "imageBox":
      return (
        <>
          <WithImage.BoxLabel {...labelProps}>
            <Input {...inputProps} data-test={dataTest} />

            {/* We use different background images for checked/unchecked state for "imageBox" view */}
            {checked ? imageChecked : imageUnchecked}
            <Paragraph mt={3} bold={checked}>
              {label}
            </Paragraph>
          </WithImage.BoxLabel>
          {displayError && <Error>{error}</Error>}
        </>
      )
      break
    case "imageRow":
      return (
        <>
          <WithImage.RowLabel {...labelProps}>
            <Input {...inputProps} data-test={dataTest} />

            {/* We use different background images for checked/unchecked state for "imageBox" view */}
            {checked ? imageChecked : imageUnchecked}
            <WithImage.Title bold={checked}>{label}</WithImage.Title>
          </WithImage.RowLabel>
          {displayError && <Error>{error}</Error>}
        </>
      )
      break
    case "default":
    default:
      return (
        <>
          <Default.Label {...labelProps}>
            <Input {...inputProps} data-test={dataTest} />

            <Default.Checkbox>{checked && <Icon name="checkMark" />}</Default.Checkbox>
            {/* Label might include a <Tooltip /> (which includes a div) so we need as="div" to prevent illegal DOM nesting */}
            <Paragraph ml={4} bold={checked} as="div">
              {label}
            </Paragraph>
          </Default.Label>
          {displayError && <Error>{error}</Error>}
        </>
      )
  }
}

type CheckboxViewProps = {
  view?: "default" | "imageBox" | "imageRow"
  imageChecked?: React.ReactNode
  imageUnchecked?: React.ReactNode
  dataTest?: string
}
export type CheckboxProps = FieldProps<string, HTMLInputElement> & CheckboxViewProps

export function CheckboxInputField({ validate = () => null, ...props }: CheckboxProps) {
  validate = getValidator(validate, props.required)

  return (
    <Field type="checkbox" px={0} py={0} {...props} validate={validate}>
      {(inputProps: DefaultFieldProps<string> & CheckboxViewProps) => <CheckboxInput {...inputProps} />}
    </Field>
  )
}
